/** Wire-shape normalizer for role permission templates returned by `GET /api/user-management/v1/roles/{id}`. */

import type { Permission } from '@/userManagement/domain/entities/Permission';

interface RoleCategoryFeatureWire {
  featureId?: unknown;
  featureName?: unknown;
  category?: unknown;
  allowRead?: unknown;
  allowWrite?: unknown;
  allowDelete?: unknown;
  allowCentralAccess?: unknown;
  allowExcelExport?: unknown;
}

interface RoleCategoryWire {
  categoryId?: unknown;
  categoryName?: unknown;
  name?: unknown;
  features?: unknown;
}

interface RolePermissionTemplateWire {
  categories?: unknown;
  permissionTemplate?: unknown;
  permissions?: unknown;
}

function asBool(v: unknown): boolean {
  return v === true;
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function buildPermissionFromFeatureRow(row: RoleCategoryFeatureWire, fallbackCategory: string): Permission | null {
  const featureId = asNumber(row.featureId);
  if (featureId == null) return null;
  return {
    featureId,
    featureName: asString(row.featureName),
    category: asString(row.category) || fallbackCategory,
    allowRead: asBool(row.allowRead),
    allowWrite: asBool(row.allowWrite),
    allowDelete: asBool(row.allowDelete),
    allowCentralAccess: asBool(row.allowCentralAccess),
    allowExcelExport: asBool(row.allowExcelExport)
  };
}

function fromCategorizedShape(categories: unknown): Permission[] | undefined {
  if (!Array.isArray(categories)) return undefined;
  const out: Permission[] = [];
  for (const cat of categories as RoleCategoryWire[]) {
    if (!cat || typeof cat !== 'object') continue;
    const categoryName = asString(cat.categoryName) || asString(cat.name);
    const features = Array.isArray(cat.features) ? (cat.features as RoleCategoryFeatureWire[]) : [];
    for (const f of features) {
      if (!f || typeof f !== 'object') continue;
      const perm = buildPermissionFromFeatureRow(f, categoryName);
      if (perm) out.push(perm);
    }
  }
  return out.length > 0 ? out : undefined;
}

function fromFlatArrayShape(rows: unknown): Permission[] | undefined {
  if (!Array.isArray(rows)) return undefined;
  const out: Permission[] = [];
  for (const r of rows as RoleCategoryFeatureWire[]) {
    if (!r || typeof r !== 'object') continue;
    const perm = buildPermissionFromFeatureRow(r, '');
    if (perm) out.push(perm);
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Resolution order:
 *   1. New nested `categories[].features[]` (current UM API)
 *   2. `permissionTemplate` flat array (legacy)
 *   3. `permissions` flat array (legacy alias used by some user-permissions paths)
 *
 * Returns `undefined` when nothing recognizable is present so the existing
 * "no template -> deny-all from catalog" branch in `permissionMapper.matrixRowsForRolePermissionTemplate`
 * still triggers correctly. The caller is responsible for enriching `featureName` / `category` from the
 * features catalog if needed (the flat-array fallbacks may not include those fields).
 */
export function normalizeRolePermissionTemplate(raw: unknown): Permission[] | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const wire = raw as RolePermissionTemplateWire;

  const fromCategories = fromCategorizedShape(wire.categories);
  if (fromCategories) return fromCategories;

  const fromTemplate = fromFlatArrayShape(wire.permissionTemplate);
  if (fromTemplate) return fromTemplate;

  const fromPermissions = fromFlatArrayShape(wire.permissions);
  if (fromPermissions) return fromPermissions;

  return undefined;
}
